import type { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import type { TranslationKey } from '@heidi/runtime/i18n';
import type {
  ChangeEvent,
  ComponentType,
  Dispatch,
  RefObject,
  SetStateAction,
} from 'react';

import type { TemplateEditorTabType } from './components/personalization/preview/template-editor-tab-view-props';
import type { OnboardingV2AssetsResponse } from './onboardingV2AssetsTypes';
import type { PersonalizationFormData } from './personalizationTypes';

export type OnboardingEmrOption = { uniqueId: string; name: string; icon?: string };

export type OnboardingComplianceBadge = {
  iconSrc: string;
  alt: string;
  text: TranslationKey;
};

export type TemplateEditorTabProps = {
  content: string;
  onContentChange?: (content: string) => void;
  tabType: TemplateEditorTabType;
  placeholder?: string;
  isEditable?: boolean;
  onSaved?: () => void;
};

export type TemplateReviewStateInput = {
  templateName?: string;
  templateContent?: string;
  specialtyName?: string;
  onContentChange?: (content: string) => void;
  onTemplateNameChange?: (name: string) => void;
};

export type TemplateReviewState = {
  activeTab: 'example' | 'structure';
  setActiveTab: Dispatch<SetStateAction<'example' | 'structure'>>;
  localTemplateName: string;
  titleInputRef: RefObject<HTMLInputElement>;
  handleTitleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  localExampleContent: string;
  setLocalExampleContent: (content: SetStateAction<string | null>) => void;
  localStructureContent: string;
  handleStructureChange: (content: string) => void;
  showSaved: boolean;
  handleSaved: () => void;
  isGeneratingExampleNote: boolean;
};

export interface OnboardingHost {
  isPracticeInviteFlowEnabled: () => boolean | undefined;
  isAppleCompliantBuild: () => boolean;
  isProductionBuild: () => boolean;
  useIsOnboardingGuidedTourEnabled: () => boolean;
  useIsOnboardingResumeEnabled: () => boolean;
  useOnboardingGeoCountryDefault: (
    setFormData: Dispatch<SetStateAction<PersonalizationFormData>>,
    options?: { hasUserSetCountry?: boolean }
  ) => { markCountryAsUserSet: () => void };
  trackTourScreenViewed: (screenName: ScreenName) => void;
  useQuerySpecialtyOnboardingV2Assets: (
    specialtyName: string | null | undefined,
    locale?: string
  ) => { data: OnboardingV2AssetsResponse | undefined; isPending: boolean };
  useSelectedSpecialtyCanonicalName: () => string | undefined;
  useLocalizedSpecialtyName: () => string | undefined;
  useSelectedEmrOption: (
    emrEhr: string | null | undefined,
    countryCode: string | undefined
  ) => OnboardingEmrOption | undefined;
  useTemplateReviewState: (input: TemplateReviewStateInput) => TemplateReviewState;
  TemplateEditorTab: ComponentType<TemplateEditorTabProps>;
  TemplateAssemblyLoader: ComponentType<{ className?: string }>;
  getOnboardingComplianceBadges: (countryCode?: string) => OnboardingComplianceBadge[];
}

let loader: (() => OnboardingHost) | null = null;
let resolved: OnboardingHost | null = null;

export function configureOnboardingHost(next: () => OnboardingHost): void {
  loader = next;
  resolved = null;
}

export function getOnboardingHost(): OnboardingHost {
  if (resolved) return resolved;
  if (!loader) throw new Error('onboarding host is not configured');
  resolved = loader();
  return resolved;
}
